"use client";
import React from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Target01Icon, ArrowRight01Icon, CheckmarkCircle02Icon } from "@hugeicons/core-free-icons";
import { ProjectType } from "@/components/utils/types";

interface ChallengesProps {
  project: ProjectType;
}

export const Challenges = ({ project }: ChallengesProps) => {
  return (
    <section className="relative w-full py-24 md:py-40 z-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="text-center mb-20 md:mb-28 relative z-10"
        >
          <h2 className="font-recoleta text-4xl md:text-6xl lg:text-7xl text-black dark:text-white mb-6">
            Challenges &{" "}
            <span className="relative inline-block">
              <span className="bg-linear-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
                Solutions
              </span>
              <motion.span
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 20,
                  delay: 0.5,
                }}
                className="absolute -bottom-2 left-0 w-full h-1 bg-linear-to-r from-sky-500 to-blue-600 rounded-full origin-left"
              />
            </span>
          </h2>
        </motion.div>

        {/* Challenges List */}
        <div className="flex flex-col gap-8 md:gap-10">
          {project.challenges?.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 60, rotateX: -10 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: idx * 0.2,
              }}
              whileHover={{
                y: -8,
                transition: { type: "spring", stiffness: 300, damping: 20 },
              }}
              className="group relative"
              style={{ perspective: "1000px" }}
            >
              {/* Card Container */}
              <div className="relative p-8 md:p-12 rounded-4xl border border-black/5 dark:border-white/10 bg-white/60 dark:bg-white/5 backdrop-blur-2xl overflow-hidden transition-all duration-500 group-hover:border-transparent group-hover:shadow-2xl group-hover:shadow-rose-500/10">
                {/* Animated Border Gradient */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  className="absolute inset-0 rounded-4xl p-px bg-linear-to-r from-rose-500/20 to-emerald-500/20 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                >
                  <div className="w-full h-full rounded-4xl bg-white dark:bg-[#0a0a0a]" />
                </motion.div>

                {/* Number Badge */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  transition={{
                    delay: idx * 0.2 + 0.3,
                    type: "spring",
                    stiffness: 200,
                  }}
                  className="absolute top-6 right-6 md:top-8 md:right-8 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-linear-to-br from-rose-500/10 to-orange-500/10 dark:from-rose-500/20 dark:to-orange-500/20 border border-rose-500/20 flex items-center justify-center"
                >
                  <span className="font-recoleta text-xl md:text-2xl font-bold bg-linear-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </motion.div>

                <div className="relative z-10 flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-0">
                  {/* Challenge Side */}
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 20,
                      delay: idx * 0.2 + 0.2,
                    }}
                    className="flex-1 flex flex-col gap-5 lg:pr-12"
                  >
                    <div className="flex items-center gap-4">
                      <motion.div
                        whileHover={{
                          scale: 1.15,
                          rotate: 10,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 15,
                          },
                        }}
                        whileTap={{
                          scale: 0.95,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 15,
                          },
                        }}
                        className="relative p-2 rounded-lg bg-linear-to-br from-rose-500/20 to-orange-500/20 text-rose-500 dark:text-rose-400 shadow-lg shadow-rose-500/10"
                      >
                        <HugeiconsIcon icon={Target01Icon} className="w-4 h-4 md:w-5 md:h-5" />
                        <motion.div
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.5, 0, 0.5],
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="absolute inset-0 rounded-2xl bg-rose-500/20"
                        />
                      </motion.div>
                      <div className="flex flex-col">
                        <span className="font-outfit text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-[0.2em]">
                          The Challenge
                        </span>
                        <motion.div
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 100,
                            damping: 20,
                            delay: idx * 0.2 + 0.4,
                          }}
                          className="h-0.5 w-20 mt-1 bg-linear-to-r from-rose-500 to-transparent origin-left"
                        />
                      </div>
                    </div>
                    <h3 className="font-recoleta text-2xl md:text-3xl text-black dark:text-white leading-snug group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors duration-300">
                      {item.challenge}
                    </h3>
                  </motion.div>

                  {/* Connector (Desktop) */}
                  <div className="hidden lg:flex flex-col items-center justify-center px-8 gap-2">
                    <motion.div
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 20,
                        delay: idx * 0.2 + 0.5,
                      }}
                      className="w-px h-12 bg-linear-to-b from-zinc-300 dark:from-zinc-700 to-transparent origin-top"
                    />
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: idx * 0.2 + 0.6,
                      }}
                      whileHover={{
                        scale: 1.2,
                        rotate: 180,
                        transition: {
                          type: "spring",
                          stiffness: 300,
                          damping: 15,
                        },
                      }}
                      className="relative p-3 rounded-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 shadow-md shadow-zinc-200/50 dark:shadow-black/50"
                    >
                      <HugeiconsIcon icon={ArrowRight01Icon} className="w-5 h-5" />
                      <motion.div
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.2, 0, 0.2],
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="absolute inset-0 rounded-full bg-zinc-500/20"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 20,
                        delay: idx * 0.2 + 0.7,
                      }}
                      className="w-px h-12 bg-linear-to-b from-transparent to-zinc-300 dark:to-zinc-700 origin-top"
                    />
                  </div>

                  {/* Divider (Mobile) */}
                  <div className="lg:hidden flex items-center justify-center gap-4 py-4">
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 20,
                        delay: idx * 0.2 + 0.4,
                      }}
                      className="flex-1 h-px bg-linear-to-r from-transparent via-zinc-300 dark:via-zinc-700 to-transparent"
                    />
                    <motion.div
                      initial={{ scale: 0, rotate: -90 }}
                      whileInView={{ scale: 1, rotate: 90 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: idx * 0.2 + 0.5,
                      }}
                      className="p-2 rounded-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 shadow-sm"
                    >
                      <HugeiconsIcon icon={ArrowRight01Icon} className="w-4 h-4" />
                    </motion.div>
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 20,
                        delay: idx * 0.2 + 0.6,
                      }}
                      className="flex-1 h-px bg-linear-to-r from-transparent via-zinc-300 dark:via-zinc-700 to-transparent"
                    />
                  </div>

                  {/* Solution Side */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 20,
                      delay: idx * 0.2 + 0.4,
                    }}
                    className="flex-1 flex flex-col gap-5 lg:pl-12"
                  >
                    <div className="flex items-center gap-4">
                      <motion.div
                        whileHover={{
                          scale: 1.15,
                          rotate: -10,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 15,
                          },
                        }}
                        whileTap={{
                          scale: 0.95,
                          transition: {
                            type: "spring",
                            stiffness: 400,
                            damping: 15,
                          },
                        }}
                        className="relative p-2 rounded-lg bg-linear-to-br from-emerald-500/20 to-teal-500/20 text-emerald-500 dark:text-emerald-400 shadow-lg shadow-emerald-500/10"
                      >
                        <HugeiconsIcon icon={CheckmarkCircle02Icon} className="w-4 h-4 md:w-5 md:h-5" />
                        <motion.div
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.5, 0, 0.5],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: 0.5,
                          }}
                          className="absolute inset-0 rounded-2xl bg-emerald-500/20"
                        />
                      </motion.div>
                      <div className="flex flex-col">
                        <span className="font-outfit text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-[0.2em]">
                          The Solution
                        </span>
                        <motion.div
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 100,
                            damping: 20,
                            delay: idx * 0.2 + 0.6,
                          }}
                          className="h-0.5 w-20 mt-1 bg-linear-to-r from-emerald-500 to-transparent origin-left"
                        />
                      </div>
                    </div>
                    <p className="font-outfit text-lg md:text-xl text-black/70 dark:text-white/70 leading-relaxed group-hover:text-black/90 dark:group-hover:text-white/90 transition-colors duration-300">
                      {item.solution}
                    </p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
